import React, { useEffect, useState } from 'react';
import { mouvementsAPI } from '../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const isMobile = () => window.innerWidth <= 768;

export default function Mouvements() {
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [mobile, setMobile] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    mouvementsAPI.getAll({ type: filterType, limit: 100 })
      .then(r => setMouvements(r.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [filterType]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: fr }); }
    catch { return '—'; }
  };

  const typeColor = (type) => {
    if (type === 'entrée') return 'var(--green)';
    if (type === 'sortie') return 'var(--red)';
    return 'var(--amber)';
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Mouvements</div>
        <div className="page-subtitle">Traçabilité complète des opérations de stock</div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <span className="toolbar-title">{mouvements.length} opération(s)</span>
          <div className="toolbar-spacer" />
          <select className="select" style={{ width: 'auto' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Tous types</option>
            <option value="entrée">Entrée</option>
            <option value="sortie">Sortie</option>
            <option value="ajustement">Ajustement</option>
            <option value="transfert">Transfert</option>
          </select>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div>Chargement...</div>
        ) : mouvements.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🔄</div>Aucun mouvement enregistré</div>
        ) : mobile ? (
          /* MOBILE — Timeline cards */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {mouvements.map(m => (
              <div key={m.id || m._id} style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--border-light)',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                {/* Icône type */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: m.type === 'entrée' ? 'var(--green-bg)' : m.type === 'sortie' ? 'var(--red-bg)' : 'var(--amber-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16,
                }}>
                  {m.type === 'entrée' ? '↓' : m.type === 'sortie' ? '↑' : '~'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{m.equipement?.nom || m.equipements?.nom || '—'}</span>
                    <span style={{ fontWeight: 700, fontSize: 16, color: typeColor(m.type) }}>
                      {m.type === 'entrée' ? '+' : m.type === 'sortie' ? '-' : '~'}{m.quantite}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span className={`badge badge-${m.type}`}>{m.type}</span>
                    {m.projet && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>📁 {m.projet}</span>}
                    {m.operateur && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>👤 {m.operateur}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {formatDate(m.created_at)} · {m.quantite_avant} → {m.quantite_apres}
                  </div>
                  {m.motif && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{m.motif}</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* DESKTOP — Table */
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date / Heure</th>
                  <th>Équipement</th>
                  <th>Type</th>
                  <th>Qté</th>
                  <th>Avant → Après</th>
                  <th>Projet</th>
                  <th>Opérateur</th>
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                {mouvements.map(m => (
                  <tr key={m.id || m._id}>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {formatDate(m.created_at)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.equipement?.nom || m.equipements?.nom || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{m.equipement?.reference || m.equipements?.reference}</div>
                    </td>
                    <td><span className={`badge badge-${m.type}`}>{m.type}</span></td>
                    <td style={{ fontWeight: 700, fontSize: 16, color: typeColor(m.type) }}>
                      {m.type === 'entrée' ? '+' : m.type === 'sortie' ? '-' : '~'}{m.quantite}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {m.quantite_avant} → {m.quantite_apres}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{m.projet || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--purple)', fontFamily: 'monospace' }}>{m.operateur || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.motif || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
